import { useState } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { IconArrowDownToLineOutline18 as Download } from "nucleo-ui-outline-18"
import { useEffectOnce } from "react-use"

import { scale } from "@productlane/lib"
import {
	AlertDialog,
	Badge,
	Button,
	IntegrationTemplate,
} from "@productlane/ui"
import { TeamsIcon } from "@productlane/ui/src/icons/project/ms-teams-icon"
import { mutators } from "@productlane/zero/mutators"

import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { env } from "@/env"
import { analytics } from "@/lib/analytics"
import { useRequiredPlans } from "@/lib/plans"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { PlanPill } from "../plan-pill"
import { HowItWorksButton } from "../settings-switches/how-it-works-button"
import { IntegrationLayout } from "./integration-layout"

const TEAMS_APP_DOWNLOAD_URL =
	"https://uploads.productlane.com/4aa80b54de997ed8e36e2a156fa3df8f.zip"

const TEAMS_DESCRIPTION = "Sync conversations from Microsoft Teams."

const TEAMS_LONG_DESCRIPTION = (
	<div>
		<div className="mb-8">
			<div className="mb-2 text-lg font-semibold text-primary">
				How it works
			</div>
			<div className="mb-6 text-sm leading-relaxed text-secondary">
				The Microsoft Teams integration syncs conversations from Teams channels
				and chats into Productlane. Add the bot to your Teams workspace, then
				messages in any channel will automatically create threads and keep
				conversations in sync.
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				Setup Instructions
			</div>
			<div className="mb-4 space-y-3 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">1.</span>
					<span>
						Download the Productlane Teams app package and install it in your
						Teams workspace.
					</span>
				</div>
				<div className="ml-5">
					<Button
						onClick={() => {
							window.open(TEAMS_APP_DOWNLOAD_URL, "_blank")
						}}
						Icon={Download}
						variant="outline"
					>
						Download Teams App (.zip)
					</Button>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">2.</span>
					<span>
						In Microsoft Teams, go to <strong>Apps</strong> &rarr;{" "}
						<strong>Manage your apps</strong> &rarr;{" "}
						<strong>Upload an app</strong> &rarr;{" "}
						<strong>Upload a custom app</strong> and select the downloaded zip
						file.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">3.</span>
					<span>
						Add the Productlane bot to the team(s) where you want to sync
						messages. Go to the team &rarr; <strong>Manage team</strong> &rarr;{" "}
						<strong>Apps</strong> &rarr; search for Productlane and add it.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">4.</span>
					<span>
						Click &quot;Connect&quot; below to link your Teams organization to
						this Productlane workspace.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">5.</span>
					<span>
						Messages in channels where the bot is installed will automatically
						create threads in Productlane. Replies sync both ways.
					</span>
				</div>
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">Features</div>
			<div className="mb-4 space-y-2 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">&bull;</span>
					<span>
						<strong className="text-primary">Automatic Sync:</strong> All
						messages in channels where the bot is installed sync to Productlane
						automatically.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">&bull;</span>
					<span>
						<strong className="text-primary">2-Way Communication:</strong> Reply
						from Productlane and it appears in Teams, and vice versa.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">&bull;</span>
					<span>
						<strong className="text-primary">Link to Linear:</strong> Connect
						support threads directly to Linear issues.
					</span>
				</div>
				{/* <div className="flex gap-2">
					<span className="text-primary">&bull;</span>
					<span>
						<strong className="text-primary">Images &amp; Files:</strong>{" "}
						Attachments from Teams are synced to Productlane automatically.
					</span>
				</div> */}
			</div>
		</div>
	</div>
)

export function TeamsIntegrationPage() {
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const navigate = useNavigate()
	const [dialogOpen, setDialogOpen] = useState(false)
	const search = useSearch({ strict: false })

	const { passes: hasScalePlan } = useRequiredPlans(scale.andUp)

	const isConnected = !!workspace?.teamsSettings

	useEffectOnce(() => {
		const success = search.success
		const error = search.error
		if (success === "true") {
			toast.success({
				title: "Success",
				description: "Microsoft Teams is now connected",
			})
			void navigate({ to: "/settings/integrations/teams", replace: true })
		} else if (error === "true") {
			toast.error({
				title: "Something went wrong",
				description: "Failed to connect Microsoft Teams. Please try again.",
			})
			void navigate({ to: "/settings/integrations/teams", replace: true })
		}
	})

	const handleDisconnect = async () => {
		if (!workspace?.teamsSettings) return

		analytics.track("disconnect-teams")
		const result = await zero.mutate(
			mutators.teamsSettings.delete({
				id: workspace.teamsSettings.id,
			}),
		).client

		if (result.type === "error") {
			toast.error({
				title: "Failed to disconnect",
				description: "Please try again.",
			})
			return
		}

		setDialogOpen(false)
	}

	return (
		<>
			<MetaInfo title="Microsoft Teams Integration" />
			<IntegrationLayout />
			<IntegrationTemplate
				logo={<TeamsIcon className="w-10" />}
				name={
					<div className="flex flex-wrap items-center gap-2">
						Microsoft Teams
						{isConnected ? (
							<Badge
								variant="green"
								size="sm"
								className="!text-green-700 dark:!text-green-400"
							>
								<span className="size-1.5 rounded-full bg-green-500" />
								Connected
							</Badge>
						) : (
							<PlanPill plan="scale" />
						)}
					</div>
				}
				description={TEAMS_DESCRIPTION}
				button={
					<div className="flex items-center gap-2">
						<HowItWorksButton
							article="93265601-8317-45fb-b06f-545ac43b140a"
							variant="icon"
						/>
						{hasScalePlan ? (
							<>
								{isConnected ? (
									<Button variant="outline" onClick={() => setDialogOpen(true)}>
										Disconnect
									</Button>
								) : (
									<RoleCheck not={["VIEWER"]}>
										<Button
											onClick={() => {
												analytics.track("connect-teams")
												window.location.href = `${env.VITE_API_URL}/api/auth/teams`
											}}
										>
											Connect
										</Button>
									</RoleCheck>
								)}
							</>
						) : (
							<UpgradeButton required={scale.andUp} />
						)}
					</div>
				}
				instructions={
					isConnected ? (
						<div className="space-y-4">
							<div className="border-border bg-muted/50 rounded-lg border p-4">
								<div className="text-sm font-medium">Connected</div>
								<div className="mt-1 text-sm text-secondary">
									Tenant ID: {workspace?.teamsSettings?.tenantId}
								</div>
							</div>

							<div className="border-border bg-muted/50 rounded-lg border p-4">
								<div className="mb-2 text-sm font-medium">
									Need to install the Teams app?
								</div>
								<Button
									onClick={() => {
										window.open(TEAMS_APP_DOWNLOAD_URL, "_blank")
									}}
									Icon={Download}
									variant="outline"
								>
									Download Teams App (.zip)
								</Button>
								<div className="mt-2 text-xs text-tertiary">
									Upload this in Teams via Apps &rarr; Manage your apps &rarr;
									Upload a custom app
								</div>
							</div>
						</div>
					) : (
						TEAMS_LONG_DESCRIPTION
					)
				}
			/>

			<AlertDialog
				variant="positive"
				isOpen={dialogOpen}
				onOpenChange={setDialogOpen}
				confirmText="Disconnect"
				title="Disconnect Microsoft Teams?"
				description="Are you sure you want to disconnect Microsoft Teams? Your data will remain saved, but the integration will be disabled."
				onCancel={() => setDialogOpen(false)}
				onConfirm={handleDisconnect}
			/>
		</>
	)
}
